import React, { useState } from "react";
import {
  Box,
  Card,
  IconButton,
  Typography,
  useMediaQuery,
  Button,
} from "@mui/material";
import { ArrowBackIosNew, ArrowForwardIos } from "@mui/icons-material";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";

const GOLD = "#FFD700";

const TestimonialsCarousel = ({ testimonials }) => {
  const isTablet = useMediaQuery("(max-width:900px)");
  const isMobile = useMediaQuery("(max-width:600px)");

  const itemsToShowDesktop = 3;
  const itemsToShowTablet = 2;
  const itemsToShowMobile = 1;

  const itemsToShow = isMobile
    ? itemsToShowMobile
    : isTablet
      ? itemsToShowTablet
      : itemsToShowDesktop;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [expandedIndex, setExpandedIndex] = useState(null);

  const totalItems = testimonials.length;
  if (!totalItems) return <Typography>No testimonials available.</Typography>;

  const maxIndex = Math.max(totalItems - itemsToShow, 0);

  const handleNext = () => {
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
  };

  return (
    <Box
      sx={{
        position: "relative",
        width: "auto",

        mx: "auto",
        py: { xs: 3, md: 5 },
        backgroundColor: "#ffffff",
      }}
    >
      <Box sx={{ overflow: "hidden" }}>
        <Box
          sx={{
            display: "flex",
            transition: "transform 0.5s ease",
            transform: `translateX(-${currentIndex * (100 / itemsToShow)}%)`,
          }}
        >
          {testimonials.map((item, idx) => {
            const isExpanded = expandedIndex === idx;

            // Allow multi-paragraph quotes; split on blank line / double newline
            const paragraphs = item.quote
              .split(/\n\s*\n/)
              .map((p) => p.trim())
              .filter(Boolean);

            const needsReadMore =
              paragraphs.length > 1 || item.quote.length > 220;

            const company = item.company || item.institution; // support either key

            return (
              <Box
                key={idx}
                sx={{
                  flex: `0 0 calc(${100 / itemsToShow}% - 16px)`,
                  mr: 2,
                }}
              >
                <Card
                  sx={{
                    borderRadius: 4,
                    boxShadow: 3,
                    bgcolor: "#ffffff",
                    border: `1px solid ${GOLD}33`,
                    p: { xs: 2.5, md: 3 },
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  {/* Top: quote icon + testimonial text */}
                  <Box>
                    <FormatQuoteIcon
                      sx={{
                        fontSize: 40,
                        color: GOLD,
                        mb: 1,
                      }}
                    />

                    {!isExpanded ? (
                      // Collapsed state: first paragraph, line-clamped
                      <Typography
                        sx={{
                          fontSize: 16,
                          lineHeight: 1.7,
                          color: "#111111",
                          fontStyle: "italic",
                          display: "-webkit-box",
                          overflow: "hidden",
                          WebkitBoxOrient: "vertical",
                          WebkitLineClamp: 4,
                        }}
                      >
                        {paragraphs[0]}
                      </Typography>
                    ) : (
                      // Expanded state: all paragraphs
                      paragraphs.map((p, pIdx) => (
                        <Typography
                          key={pIdx}
                          sx={{
                            fontSize: 16,
                            lineHeight: 1.7,
                            color: "#111111",
                            fontStyle: "italic",
                            mt: pIdx === 0 ? 0 : 1.5,
                          }}
                        >
                          {p}
                        </Typography>
                      ))
                    )}

                    {needsReadMore && (
                      <Button
                        size="small"
                        onClick={() =>
                          setExpandedIndex(isExpanded ? null : idx)
                        }
                        sx={{
                          mt: 1,
                          p: 0,
                          minWidth: "auto",
                          textTransform: "none",
                          fontSize: 13,
                          color: GOLD,
                        }}
                      >
                        {isExpanded ? "Show less" : "Read more"}
                      </Button>
                    )}
                  </Box>

                  {/* Bottom: name, role, company */}
                  <Box
                    sx={{
                      mt: 3,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                    }}
                  >
                    {/* Left: name + role, nicely spaced */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: 16,
                          color: "#000000",
                        }}
                      >
                        {item.name}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 13,
                          color: "#444444",
                        }}
                      >
                        {item.role}
                      </Typography>
                    </Box>

                    {/* Right: logo more prominent, occupying the bottom space */}
                    {item.logo ? (
                      <Box
                        sx={{
                          flex: 1,
                          display: "flex",
                          justifyContent: "flex-end",
                          alignItems: "center",
                        }}
                      >
                        <Box
                          component="img"
                          src={item.logo}
                          alt={company ? `${company} logo` : "Company logo"}
                          sx={{
                            width: "100%", // let it stretch within its side
                            maxWidth: 160, // make it “longer”
                            height: "auto",
                            objectFit: "contain",
                          }}
                        />
                      </Box>
                    ) : (
                      company && (
                        <Typography
                          sx={{
                            ml: "auto",
                            letterSpacing: 3,
                            fontWeight: 700,
                            fontSize: 11,
                            textTransform: "uppercase",
                            color: GOLD,
                            textAlign: "right",
                          }}
                        >
                          {company}
                        </Typography>
                      )
                    )}
                  </Box>
                </Card>
              </Box>
            );
          })}
        </Box>
      </Box>

      {totalItems > itemsToShow && (
        <>
          <IconButton
            onClick={handlePrev}
            sx={{
              position: "absolute",
              top: "50%",
              left: { xs: 4, md: -15 },
              transform: "translateY(-50%)",
              bgcolor: "#ffffff",
              "&:hover": { bgcolor: "#fff7cc" },
              border: `1px solid ${GOLD}`,
              boxShadow: 2,
            }}
            size={isMobile ? "small" : "medium"}
          >
            <ArrowBackIosNew fontSize="small" sx={{ color: "#000000" }} />
          </IconButton>

          <IconButton
            onClick={handleNext}
            sx={{
              position: "absolute",
              top: "50%",
              right: { xs: 4, md: -15 },
              transform: "translateY(-50%)",
              bgcolor: "#ffffff",
              "&:hover": { bgcolor: "#fff7cc" },
              border: `1px solid ${GOLD}`,
              boxShadow: 2,
            }}
            size={isMobile ? "small" : "medium"}
          >
            <ArrowForwardIos fontSize="small" sx={{ color: "#000000" }} />
          </IconButton>
        </>
      )}
    </Box>
  );
};

export default TestimonialsCarousel;
